import {
    FLOW_TYPE,
    MAIN_CANVAS_ID,
    DECISION_TYPE,
    EDGE_TYPE,
    GRID_SIZE,
} from '../utils/editor.constants';
import { createDecision } from './decision.api';
import {
    findIncomingCells,
    findIncomingEdges,
    findOutgoingCells,
    findOutgoingEdges,
} from '../utils/cell/automation-edge-utils';
import { maxCellIdStore } from '../utils/max-cell-id-utils';

export const DECISION_CELL_SIZE = 32;

export const createDecisionsAsNecessary = async (cellId, cells) => {
    const connectedCells = findOutgoingCells(cellId, cells);
    const flowCells = connectedCells.filter(({ type }) => type === FLOW_TYPE);
    const flowCellIds = flowCells.map(({ id }) => id);
    let decisionItems = [];
    let decisionFlowIdItems = [];
    const decisionDiamondCell = cells.find((cell) => cell.id === cellId);

    if (decisionDiamondCell?.value?.decisions?.length) {
        decisionItems = decisionDiamondCell?.value?.decisions?.filter((item) => item !== null && 'flowId' in item && 'decisionId' in item);
        decisionFlowIdItems = decisionItems.map((item) => {
            return `${item.flowId}`;
        });
    }

    await Promise.all(flowCellIds.map(async (flowCellId) => {
        if (decisionFlowIdItems.includes(`${flowCellId}`)) return null;

        const data = await createDecision();

        decisionItems.push({
            flowId: `${flowCellId}`,
            decisionId: data,
        });

        return data;
    }));

    const decisionIds = [];
    const flowIds = [];

    decisionItems.forEach(({ decisionId, flowId }) => {
        decisionIds.push(decisionId);
        flowIds.push(flowId);
    });

    return { decisionIds, flowIds };
};

export const createDecisionEdge = (cells, source, target) => {
    maxCellIdStore.increment();

    return {
        meta: {
            id: `${maxCellIdStore.maxCellId}`,
            parent: MAIN_CANVAS_ID,
            style: EDGE_TYPE,
            edge: '1',
            source,
            target,
        },
        id: `${maxCellIdStore.maxCellId}`,
        type: EDGE_TYPE,
        mxGeometry: {
            as: 'geometry',
            height: 64,
            width: 64,
        },
    };
};

export const createDecisionCell = async (flowIds, x, y) => {
    const decisionItems = await Promise.all(flowIds.map(async (flowCellId) => {
        const data = await createDecision();

        return {
            flowId: `${flowCellId}`,
            decisionId: data,
        };
    }));

    maxCellIdStore.increment();

    return {
        id: maxCellIdStore.maxCellId,
        type: DECISION_TYPE,
        meta: {
            id: maxCellIdStore.maxCellId,
            parent: MAIN_CANVAS_ID,
            style: DECISION_TYPE,
            vertex: '1',
        },
        mxGeometry: {
            x: x && Math.floor(x / GRID_SIZE) * GRID_SIZE,
            y: y && Math.floor(y / GRID_SIZE) * GRID_SIZE,
            width: DECISION_CELL_SIZE,
            height: DECISION_CELL_SIZE,
            as: 'geometry',
        },
        value: {
            as: 'value',
            initialized: '1',
            published: '0',
            decisions: decisionItems,
            name: 'Decision Diamond',
        },
    };
};

export const handleDecisionDiamondCreationAndUpdate = async ({
    newCell, source, target, draftJson,
}) => {
    const sourceElement = draftJson.cells.find((cell) => cell.meta.id === source);
    const sourceType = sourceElement.type;
    const targetElement = draftJson.cells.find((cell) => cell.meta.id === target);
    const targetType = targetElement.type;

    if (sourceType && sourceType !== DECISION_TYPE && targetType === FLOW_TYPE) {
        const connectedCells = findOutgoingCells(source, draftJson.cells);
        const decision = connectedCells.find((connectedCell) => connectedCell.type === DECISION_TYPE);

        if (!decision) {
            const otherSequenceCells = connectedCells.filter(({ type }) => type === FLOW_TYPE);

            if (otherSequenceCells.length > 0) {
                const { mxGeometry } = sourceElement;

                const axisX = mxGeometry.x + (GRID_SIZE * 2);
                const axisY = mxGeometry.y;

                const otherSequenceCellIds = otherSequenceCells.map((cell) => cell.id);

                const decisionCell = await createDecisionCell([...otherSequenceCellIds, newCell.meta.target], axisX, axisY);

                newCell.meta.source = decisionCell.id;

                const outgoingEdges = findOutgoingEdges(sourceElement.id, draftJson.cells);
                const edgesToOtherSequences = outgoingEdges.filter((edge) => {
                    return otherSequenceCellIds.includes(edge.meta.target);
                });

                edgesToOtherSequences.forEach((edge) => {
                    const edgeIndex = draftJson.cells.findIndex((cell) => cell.id === edge.id);

                    draftJson.cells[edgeIndex].meta.source = decisionCell.id;
                });

                const newEdge = createDecisionEdge(draftJson.cells, source, decisionCell.id);

                draftJson.cells = [...draftJson.cells, newEdge, decisionCell];
            }
        } else {
            newCell.meta.source = decision.id;
        }
    }

    return { cellMetaSource: newCell.meta.source, ddDraftJson: draftJson };
};

export const processDecisionCells = (filteredDecisionCells, updatedCells) => {
    for (const decision of filteredDecisionCells) {
        const connectedCellsToDecisionDiamond = findOutgoingCells(decision.id, updatedCells);

        if (connectedCellsToDecisionDiamond.length === 1) {
            const incomingEdgeId = findIncomingEdges(decision.id, updatedCells)[0].id;
            const outgoingEdgeId = findOutgoingEdges(decision.id, updatedCells)[0].id;
            const incomingCellId = findIncomingCells(decision.id, updatedCells)[0].id;
            const stillConnectedSequenceId = connectedCellsToDecisionDiamond[0].id;

            const newEdge = createDecisionEdge(updatedCells, incomingCellId, stillConnectedSequenceId);

            updatedCells = [...updatedCells, newEdge];
            const elementsIdsToDelete = [incomingEdgeId, outgoingEdgeId, decision.id];

            updatedCells = updatedCells.filter((cell) => !elementsIdsToDelete.includes(cell.id));
        }
    }

    return updatedCells;
};


// given an array of cells, check that if the selection contains a decision diamond, it
// includes a connected "when" and two outgoing sequences.
export const containsIncompleteDecisionDiamond = (cells) => {
    const decisionDiamonds = cells.filter((cell) => cell.type === DECISION_TYPE);

    if (!decisionDiamonds.length) return false;

    return decisionDiamonds.find((decisionDiamond) => {
        const outgoingElements = findOutgoingCells(decisionDiamond.id, cells);
        const incomingElements = findIncomingCells(decisionDiamond.id, cells);

        return (!incomingElements.length || outgoingElements.length < 2);
    });
};
